import React from 'react';
import { connect } from 'react-redux';
import { FormInstance } from 'antd/lib/form';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { v4 } from 'uuid';
import { Checkbox, Alert } from 'antd';
import { CheckboxChangeEventTarget } from 'antd/lib/checkbox/Checkbox';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { AppThunk, RootState } from 'src/store';
import {
  IMenuState,
  IReviewBookState,
  getBookPackageWithCard,
  setGuests,
  ICouponState,
  setPriceChangedError,
  getNavigationMenu,
  setBooking,
  setBookingComplete,
} from 'src/store/slices/index';
import { BlueButton } from 'src/components/components/index';
import {
  IBookingComplete,
  IBookPackageWithCard,
  IGuestsBooking,
  ISessionKey,
  IFormErrors,
  ScrollBehaviorEnum,
} from 'src/common-types/index';
import { UrlUtils, getSessionObj, renderNumber, getFullUrl } from 'src/utils/index';
import {
  DEFAULT_GUEST_DATA,
  SESSION_KEY_LABEL_ROOMS,
  SESSION_KEY_LABEL,
  Routes,
  HOTEL_CONFIRMED_INFO,
  HOTEL_BOOKING_COMPLETE,
  HOTEL_PACKAGE_ID,
} from 'src/constants/index';
import { GuestInfo } from '../guest-info-wrapper/guest-info';
import { PaymentMethod } from '../payment-method';
import { ConfirmationEmail } from '../confirmation-email';
import { CancellationPolicies } from '../cancellation-policies';
import {
  ReservationProgress,
  BookingConfirmed,
  BookingFailed,
} from 'src/components/components/index';
import './style.scss';

interface IMapStateToProps {
  reviewBookStore: IReviewBookState;
  redeemCodeStore: ICouponState;
  menuStore: IMenuState;
}

interface IMapDispatchToProps {
  getBookPackageWithCard: (bookWithCard: IBookPackageWithCard) => void;
  setGuests: (guests: IGuestsBooking[]) => void;
  setPriceChangedError: (priceChanged: boolean) => void;
  getNavigationMenu: () => AppThunk;
  setBooking: (data: IBookingComplete) => void;
  setBookingComplete: (item: boolean) => void;
}

interface IProps
  extends IMapStateToProps,
    IMapDispatchToProps,
    WrappedComponentProps,
    RouteComponentProps {}

interface IState {
  isAccept: boolean;
  isAcceptBookingRemarks: boolean;
  isCheckMainGuest: boolean;
}

const ZERO = 0;
const SECOND_ELEMENT = 2;
const TEN_SEC = 10000;
const PRIVACY = 'privacy1';

class ReviewBookComponent extends React.Component<IProps, IState> {
  state: IState = {
    isAccept: false,
    isAcceptBookingRemarks: false,
    isCheckMainGuest: true,
  };

  formPaymentRef = React.createRef<FormInstance>();
  formGuestRef = React.createRef<FormInstance>();
  formEmailRef = React.createRef<FormInstance>();

  onChangeAccept = (e: { target: CheckboxChangeEventTarget }): void => {
    this.setState({ isAccept: e.target.checked });
  };

  onChangeAcceptBookingRemarks = (e: { target: CheckboxChangeEventTarget }): void => {
    this.setState({ isAcceptBookingRemarks: e.target.checked });
  };

  getGuests = () => {
    return this.props.reviewBookStore.bookingSummary.bookingCard.package.rooms
      .map((room, index) => {
        const guests = [...new Array(room.adultsCount)].map((_, indexGuest) => {
          return indexGuest === ZERO
            ? {
                ...DEFAULT_GUEST_DATA,
                id: v4(),
                isMainGuest: index === ZERO && indexGuest === ZERO,
                allocation: room.id,
                type: 'adult',
              }
            : {
                ...DEFAULT_GUEST_DATA,
                id: v4(),
                allocation: room.id,
                type: 'adult',
              };
        });

        const kids = room.kidsAges.map((kidAge) => {
          return {
            ...DEFAULT_GUEST_DATA,
            id: v4(),
            allocation: room.id,
            type: 'kid',
            age: kidAge,
          };
        });

        return [...guests, ...kids];
      })
      .flat();
  };

  onCompleteBooking = () => {
    const { bookingSummary, card, guests } = this.props.reviewBookStore;
    const { coupon } = this.props.redeemCodeStore;
    const values = UrlUtils.getValues();
    const sessionKey = getSessionObj(
      values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
      values[SESSION_KEY_LABEL] as ISessionKey,
    );
    const getLeadPax = guests.find((guest) => guest.isMainGuest);

    if (getLeadPax === undefined) {
      this.setState({ isCheckMainGuest: false });
      window.scrollTo(ZERO, ZERO);
    }

    const data = {
      sessionKey,
      bookingPrice: bookingSummary?.bookingCard.bookingPrice.payNowPrice,
      hotelId: bookingSummary?.bookingCard.package.hotelId,
      leadPaxId: getLeadPax?.id,
      leadPaxRoomId: getLeadPax?.allocation,
      packageId: bookingSummary?.bookingCard.package.packageId,
      couponCode: coupon,
      card: { ...card, phone: getLeadPax?.phone },
      guests: guests.map((guest) => {
        const type: string = guest?.type;

        return {
          ...guest,
          type,
        };
      }),
    };

    this.setFieldsGuestsValue(guests);
    this.formEmailRef.current.setFieldsValue({ email: this.mainGuestData('email') });
    this.formGuestRef.current.setFieldsValue({
      mainGuestGivenName: this.mainGuestData('givenName'),
    });
    this.formGuestRef.current.setFieldsValue({
      mainGuestPhone: this.mainGuestData('phone'),
    });
    this.formGuestRef.current.setFieldsValue({ mainGuestSurname: this.mainGuestData('surname') });
    this.formPaymentRef.current.setFieldsValue({ address: card.addressLine });
    this.formPaymentRef.current.setFieldsValue({ city: card.city });
    this.formPaymentRef.current.setFieldsValue({ state: card.state });
    this.formPaymentRef.current.setFieldsValue({ 'postal-code': card.zipCode });

    const formPaymentRef = this.formPaymentRef.current.validateFields();
    const formGuestRef = this.formGuestRef.current.validateFields();
    const formEmailRef = this.formEmailRef.current.validateFields();

    Promise.all([formPaymentRef, formGuestRef, formEmailRef])
      .then(() => {
        this.props.getBookPackageWithCard(data);
      })
      .catch((error: IFormErrors) => {
        const { errorFields } = error;
        const { name } = errorFields[ZERO];

        const { scrollToField: scrollToPaymentField } = this.formPaymentRef.current;
        const { scrollToField: scrollToGuestField } = this.formGuestRef.current;
        const { scrollToField: scrollToEmailField } = this.formEmailRef.current;

        (() => {
          scrollToEmailField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
          scrollToPaymentField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
          scrollToGuestField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
        })();
      });
  };

  setFieldsGuestsValue = (guests: IGuestsBooking[]): void[] => {
    return guests.map((guest, index) => {
      if (guest.isMainGuest === true) {
        return;
      }

      this.formGuestRef.current.setFieldsValue({ [`guests[${index}].givenName`]: guest.givenName });
      this.formGuestRef.current.setFieldsValue({ [`guests[${index}].surname`]: guest.surname });
    });
  };

  mainGuestData = (item: string): string => {
    const guest = this.props.reviewBookStore.guests.find((item) => item.isMainGuest === true);

    return guest ? (guest[item] as string) : '';
  };

  titleBookingFailed = (): string => {
    return `${this.props.intl.formatMessage({ id: 'your.booking.in' })}
      ${this.props.reviewBookStore.bookingSummary.bookingCard.name}
      ${this.props.intl.formatMessage({ id: 'did.not.go.through' })}`;
  };

  redirectToHotelPage = (): void => {
    const hotelId = Number(this.props.history.location.pathname.split('/')[SECOND_ELEMENT]);
    this.props.history.push(`${Routes.Hotel}/${hotelId}/${location.search}`);
    this.props.setPriceChangedError(false);
  };

  getErrorMessages = () =>
    this.props.reviewBookStore.errorsBookingMessage.map((message, index) => (
      <p key={index}>{message.errorMessage}</p>
    ));

  componentDidMount() {
    this.props.setGuests(this.getGuests());

    if (localStorage.getItem(HOTEL_CONFIRMED_INFO) !== null) {
      const bookingInfo = localStorage.getItem(HOTEL_CONFIRMED_INFO);
      const bookingCompleteInfo = localStorage.getItem(HOTEL_BOOKING_COMPLETE);

      this.props.setBooking(JSON.parse(bookingInfo));
      this.props.setBookingComplete(JSON.parse(bookingCompleteInfo));
    }
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const { bookingComplete, errorBooking, bookingSummary } = this.props.reviewBookStore;
    const { bookingCard } = bookingSummary;

    if (
      (bookingComplete && bookingComplete !== prevProps.reviewBookStore.bookingComplete) ||
      (errorBooking && errorBooking !== prevProps.reviewBookStore.errorBooking)
    ) {
      localStorage.setItem(HOTEL_PACKAGE_ID, JSON.stringify(bookingCard.package.packageId));
      setTimeout(() => {
        this.props.getNavigationMenu();
      }, TEN_SEC);
    }
  }

  render(): React.ReactNode {
    const { menuStore, reviewBookStore, intl } = this.props;
    const {
      guests,
      loadingBooking,
      bookingComplete,
      bookingSummary,
      isPriceChangedError,
      isSoldOutError,
      errorsBookingMessage,
      isBookingInProgress,
    } = reviewBookStore;
    const isNotification =
      bookingComplete ||
      loadingBooking ||
      isPriceChangedError ||
      isSoldOutError ||
      isBookingInProgress;

    return (
      guests && (
        <>
          <div className={`review-book ${isNotification ? 'hide-block' : ''}`}>
            {errorsBookingMessage && errorsBookingMessage.length && (
              <Alert
                message={<FormattedMessage id="transaction.failed" />}
                description={this.getErrorMessages()}
                type="error"
                showIcon
              />
            )}

            <GuestInfo formRef={this.formGuestRef} isCheckMainGuest={this.state.isCheckMainGuest} />
            <PaymentMethod formRef={this.formPaymentRef} />
            <ConfirmationEmail formRef={this.formEmailRef} value={this.mainGuestData('email')} />
            <CancellationPolicies />
            <div className="review-book__wrapper">
              <Checkbox onChange={this.onChangeAccept} />
              <p className="review-book__accept-text">
                <FormattedMessage
                  id="accept.you.will.be.charged"
                  values={{
                    price: renderNumber(bookingSummary?.bookingCard?.bookingPrice?.payNowPrice),
                    type: <FormattedMessage id="hotel" />,
                  }}
                />
              </p>
            </div>
            <div className="review-book__wrapper">
              <Checkbox onChange={this.onChangeAcceptBookingRemarks} />
              <p className="review-book__accept-text">
                <FormattedMessage id="by.checking.this.box.you.acknowledge.that.you.have.read" />
              </p>
            </div>
            <div className="review-book__wrapper">
              <p className="review-book__agree-navigation-text">
                <FormattedMessage id="agree.booking" />
                {menuStore.items ? (
                  <a
                    className="review-book__agree-navigation-link"
                    href={getFullUrl(menuStore.items?.baseUrl, PRIVACY)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage id="privacy.policy" />
                  </a>
                ) : null}
              </p>
              <div className="review-book__complete-btn">
                <BlueButton
                  onClick={this.onCompleteBooking}
                  disabled={!this.state.isAccept || !this.state.isAcceptBookingRemarks}
                >
                  <FormattedMessage id="complete.booking" />
                </BlueButton>
              </div>
            </div>
          </div>
          {loadingBooking && <ReservationProgress isLoadingBooking={loadingBooking} />}
          {isBookingInProgress && <ReservationProgress isBookingInProgress={isBookingInProgress} />}
          <BookingConfirmed
            isBookingComplete={bookingComplete}
            packageData={reviewBookStore}
            onClick={this.redirectToHotelPage}
          />
          {isPriceChangedError && (
            <BookingFailed
              title={intl.formatMessage({ id: 'price.changed' })}
              description={intl.formatMessage({ id: 'unable.to.confirm.booking' })}
              buttonTitle={intl.formatMessage({ id: 'book.another.room' })}
              onClick={this.redirectToHotelPage}
              isPriceChangedError={isPriceChangedError}
            />
          )}
          {isSoldOutError && (
            <BookingFailed
              title={this.titleBookingFailed()}
              description={intl.formatMessage({ id: 'sorry.we.were.unable.to.confirm' })}
              buttonTitle={intl.formatMessage({ id: 'start.searching' })}
              onClick={this.redirectToHotelPage}
            />
          )}
        </>
      )
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    reviewBookStore: state.reviewBookStore,
    menuStore: state.navigationMenuStore,
    redeemCodeStore: state.redeemCodeStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getBookPackageWithCard,
  setGuests,
  setPriceChangedError,
  getNavigationMenu,
  setBooking,
  setBookingComplete,
};

export const ReviewBook = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(injectIntl(ReviewBookComponent)));

